import { useEffect, useRef, type JSX } from 'react'

import { describeMethod, formatMoney } from '../lib/format.ts'
import type { PaymentQuote } from '../lib/types.ts'

export interface PaymentConfirmProps {
  quote: PaymentQuote
  locale: string
  paying: boolean
  canChangeCard: boolean
  onPay: () => void
  onChangeCard: () => void
}

/**
 * Shows the quoted amount, fee and total for one card, with a separate Pay button.
 * @param props - The quote, the session locale, and the pay and change-card callbacks.
 * @returns The confirm screen.
 */
export function PaymentConfirm(props: PaymentConfirmProps): JSX.Element {
  const { quote, locale, paying, canChangeCard, onPay, onChangeCard } = props
  const headingRef = useRef<HTMLHeadingElement | null>(null)
  const money = (minor: number) => formatMoney(minor, quote.currency, locale)

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <section
      data-testid="payment-confirm"
      aria-labelledby="payment-confirm-heading"
      className="flex flex-col gap-3"
    >
      <h2
        id="payment-confirm-heading"
        data-testid="payment-confirm-heading"
        ref={headingRef}
        tabIndex={-1}
        className="blytz-drawer__title"
      >
        {'Confirm payment'}
      </h2>
      <p data-testid="payment-method" className="blytz-pm__label">
        {describeMethod({ ...quote.method, paymentType: 'card' })}
      </p>
      <p data-testid="payment-currency" className="blytz-pm__meta">
        {quote.currency}
      </p>
      <dl className="blytz-card blytz-card--outlined flex flex-col">
        <div className="blytz-row">
          <dt className="blytz-row__title">{'Amount'}</dt>
          <dd data-testid="payment-amount" className="blytz-row__amount">
            {money(quote.amount)}
          </dd>
        </div>
        <div className="blytz-row">
          <dt className="blytz-row__title">{'Fee'}</dt>
          <dd data-testid="payment-fee" className="blytz-row__amount">
            {money(quote.fee)}
          </dd>
        </div>
        <div className="blytz-row blytz-row--last">
          <dt className="blytz-row__title">{'Total'}</dt>
          <dd data-testid="payment-total" className="blytz-row__amount">
            {money(quote.total)}
          </dd>
        </div>
      </dl>
      <button
        type="button"
        data-testid="payment-pay"
        className={`blytz-btn blytz-btn--primary blytz-btn--full${paying ? ' blytz-btn--loading' : ''}`}
        disabled={paying}
        aria-busy={paying}
        onClick={onPay}
      >
        {paying ? 'Paying…' : `Pay ${money(quote.total)}`}
      </button>
      {canChangeCard && !paying && (
        <button
          type="button"
          data-testid="payment-change-card"
          className="blytz-btn blytz-btn--outline blytz-btn--full"
          onClick={onChangeCard}
        >
          {'Use another card'}
        </button>
      )}
    </section>
  )
}
